import { useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { appConfig } from "@/config/app";
import { cn } from "@/lib/utils";
import { BRAND } from "../brand";
import { SectionHeading } from "../SectionHeading";
import { Reveal } from "../motion/Reveal";

export type FaqItem = { q: string; a: ReactNode };

const CONTACT = appConfig.contactEmail;

const LINK =
  "font-semibold text-action underline decoration-action-border underline-offset-4 hover:decoration-action";

const QUESTIONS: FaqItem[] = [
  {
    q: "Faut-il utiliser Pennylane\u00a0?",
    a: `Oui, aujourd’hui. ${BRAND} lit vos clients, vos factures et vos règlements dans Pennylane, chaque heure. Vous utilisez un autre logiciel\u00a0? Dites-le-nous\u00a0: cela nous aide à choisir la suite.`,
  },
  {
    q: `Mes clients voient-ils ${BRAND} dans les relances\u00a0?`,
    a: "Non. Les relances partent de votre adresse Gmail, avec la signature de votre choix. Votre client reçoit un email de vous, sans mention d’un outil.",
  },
  {
    q: "La mise en demeure part-elle toute seule\u00a0?",
    a: `Non, jamais. Elle reste verrouillée tant que le retard n’a pas passé votre seuil (180 jours par défaut, réglable). Au-delà, ${BRAND} la prépare et vous la relisez. Son export en PDF et son envoi sont en préparation.`,
  },
  {
    q: "Que deviennent mes notes après un avoir ou une refacturation\u00a0?",
    a: "Elles restent. Appels, notes, promesses et réponses sont rangés sur le client, pas sur la facture\u00a0: un avoir, une refacturation ou un relettrage dans Pennylane ne les efface pas.",
  },
  {
    q: `${BRAND} peut-il modifier ma comptabilité\u00a0?`,
    a: `Non. L’accès à Pennylane est en lecture seule\u00a0: ${BRAND} n’y crée, n’y modifie et n’y supprime rien. Vous pouvez le déconnecter à tout moment.`,
  },
  {
    q: "Et l’accès à ma boîte Gmail\u00a0?",
    a: `Vous l’autorisez depuis Google, pour envoyer vos relances et repérer les emails de vos clients afin de les ranger dans leur fiche. Vous pouvez le retirer à tout moment, depuis les paramètres de l’application ou depuis votre compte Google.`,
  },
  {
    q: "Où sont hébergées mes données\u00a0?",
    a: (
      <>
        Votre base de données est hébergée dans l’Union européenne. Chaque sous-traitant
        est listé, avec son rôle et sa localisation, sur la page{" "}
        <Link to="/legal/sous-traitants" className={LINK}>
          Sous-traitants
        </Link>
        .
      </>
    ),
  },
  {
    q: "Combien ça coûte\u00a0?",
    a: (
      <>
        La création de compte est gratuite, sans carte bancaire. Les offres sont détaillées
        sur la page{" "}
        <Link to="/tarifs" className={LINK}>
          Tarifs
        </Link>
        .
      </>
    ),
  },
];

function Item({ q, a, delay }: { q: string; a: ReactNode; delay: number }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <Reveal as="li" delay={delay} className="border-b border-border">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((o) => !o)}
          className="group flex min-h-14 w-full cursor-pointer items-center justify-between gap-6 py-4 text-left text-base font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:text-lg"
        >
          {q}
          <span
            aria-hidden="true"
            className={cn(
              "grid size-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition-[rotate,background-color,color,border-color] duration-200 group-hover:border-action group-hover:text-action",
              open && "rotate-45 border-action bg-action text-action-foreground group-hover:text-action-foreground",
            )}
          >
            <Plus className="size-4" />
          </span>
        </button>
      </h3>
      <div
        id={id}
        hidden={!open}

      >
        {/* À l'ouverture, la réponse apparaît en fondu ; sa hauteur, elle, ne s'anime pas. */}
        <div className="enc-rise-sm overflow-hidden">
          <p className="max-w-[62ch] pb-5 text-base leading-relaxed text-muted-foreground">{a}</p>
        </div>
      </div>
    </Reveal>
  );
}

/** Liste de questions dépliables (accueil, tarifs). */
export function FaqList({ items }: { items: FaqItem[] }) {
  return (
    <ul className="border-t border-border">
      {items.map((item, i) => (
        <Item key={item.q} q={item.q} a={item.a} delay={i * 50} />
      ))}
    </ul>
  );
}

/** Questions fréquentes : des réponses courtes, vérifiables dans l'app. */
export function FAQ() {
  return (
    <section id="questions" className="py-24 lg:py-32">
      <div className="mx-auto grid max-w-[1200px] gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <SectionHeading
          eyebrow="Questions"
          title="Ce qu’on nous demande avant de commencer."
          lead={
            <>
              Une question qui manque&nbsp;? Écrivez à{" "}
              <a href={`mailto:${CONTACT}`} className={LINK}>
                {CONTACT}
              </a>
              .
            </>
          }
          className="lg:sticky lg:top-28 lg:self-start"
        />
        <FaqList items={QUESTIONS} />
      </div>
    </section>
  );
}
